export const DASH_DURATION_SECONDS = 0.8;
export const DASH_RECHARGE_SECONDS = 4;
export const DASH_SPEED_CAP_MULTIPLIER = 2.2;

export interface DashSnapshot {
  readonly active: boolean;
  readonly ready: boolean;
  readonly charge01: number;
  readonly remainingSeconds: number;
  readonly speedCapMultiplier: number;
}

export interface DashUpdate extends DashSnapshot {
  readonly triggered: boolean;
  readonly becameReady: boolean;
}

function requireDelta(deltaSeconds: number): number {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds < 0) {
    throw new RangeError("deltaSeconds must be a finite, non-negative number");
  }
  return deltaSeconds;
}

export class DashModel {
  private remaining = 0;
  private charge = 1;
  private previousPressed = false;

  get active(): boolean {
    return this.remaining > 0;
  }

  get ready(): boolean {
    return this.charge >= 1;
  }

  update(deltaSeconds: number, pressed: boolean): Readonly<DashUpdate> {
    const delta = requireDelta(deltaSeconds);
    const risingEdge = pressed && !this.previousPressed;
    this.previousPressed = pressed;

    let triggered = false;
    if (risingEdge && this.ready) {
      this.remaining = DASH_DURATION_SECONDS;
      this.charge = 0;
      triggered = true;
    }

    const readyBeforeProgress = this.ready;
    if (delta > 0) {
      this.remaining = Math.max(0, this.remaining - delta);
      this.charge = Math.min(1, this.charge + delta / DASH_RECHARGE_SECONDS);
    }
    const becameReady = !readyBeforeProgress && this.ready;

    return Object.freeze({
      ...this.snapshot(),
      triggered,
      becameReady,
    });
  }

  snapshot(): Readonly<DashSnapshot> {
    return Object.freeze({
      active: this.active,
      ready: this.ready,
      charge01: this.charge,
      remainingSeconds: this.remaining,
      speedCapMultiplier: this.active ? DASH_SPEED_CAP_MULTIPLIER : 1,
    });
  }

  reset(): void {
    this.remaining = 0;
    this.charge = 1;
    this.previousPressed = false;
  }
}
